import React from 'react'
import { PersonLiteDto, PersonDto } from '../../../api/person/models/PersonDto'
import {
    RecommendedCandidateLiteDto,
    CandidateRecommendationDto,
} from '../../../api/role/models/getRecommendedCandidatesForRole/GetRecommendedCandidatesForRole'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAddressCard } from '@fortawesome/free-solid-svg-icons'
import { TransformedResolvedPerson } from '../../../utils/networkTransformUtils'
import { CompanyDto } from '../../../api/company/models/CompanyDto'
import PersonProfileModal from '../../person/profile/PersonProfileModal'
import { formatPersonName } from '../../../utils/personUtils'
import CandidateConnectionsTrustedCircle from './CandidateConnectionsTrustedCircle'
import { MapExtra } from '../../../utils/MapExtra'

type Props = {
    candidate: RecommendedCandidateLiteDto
    recommendations: CandidateRecommendationDto[]
    personById: MapExtra<string, PersonLiteDto>
    connections?: TransformedResolvedPerson
    companiesById: MapExtra<string, CompanyDto>
    personFull?: PersonDto
}

export default function RecommendedCandidateCard({
    candidate,
    recommendations,
    personById,
    connections,
    companiesById,
    personFull,
}: Props): React.JSX.Element {
    const [isProfileOpen, setIsProfileOpen] = React.useState(false)
    const avatarUrl = candidate.linkedInProfilePicUrl || '/no-profile-pic.svg'

    // Build a quick lookup for recommendations by seed personId
    const recByPersonId = new MapExtra<string, CandidateRecommendationDto>()
    for (const rec of recommendations) {
        if (rec.personId) recByPersonId.set(rec.personId, rec)
    }

    return (
        <div className="flex flex-col gap-2 p-3 border rounded-lg bg-white w-full">
            <div className="flex items-center gap-3">
                <img
                    src={avatarUrl}
                    alt={formatPersonName(candidate)}
                    className="w-16 h-16 rounded-full object-cover"
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                        e.currentTarget.onerror = null
                        e.currentTarget.src = '/no-profile-pic.svg'
                    }}
                />
                <div className="min-w-0">
                    <div className="font-semibold truncate">
                        {formatPersonName(candidate)}
                    </div>
                    {candidate.headline && (
                        <div className="text-sm text-gray-600 truncate">
                            {candidate.headline}
                        </div>
                    )}
                    {candidate.location && (
                        <div className="text-xs text-gray-500 truncate">
                            {candidate.location}
                        </div>
                    )}
                </div>
            </div>

            {/* View full profile link (if full data available) */}
            {personFull && (
                <div className="mt-1">
                    <button
                        className="text-sm text-blue-500 hover:underline flex items-center gap-2"
                        onClick={() => setIsProfileOpen(true)}
                    >
                        <FontAwesomeIcon icon={faAddressCard} />
                        <span>View full profile</span>
                    </button>
                </div>
            )}

            {/* Connections with hiring team (by company) */}
            {connections && connections.companies.length > 0 && (
                <CandidateConnectionsTrustedCircle
                    connections={connections}
                    companiesById={companiesById}
                    seedPersonLitesById={personById}
                    recommendationsBySeedId={recByPersonId}
                />
            )}

            {/* Full Profile Modal */}
            {isProfileOpen && personFull && (
                <PersonProfileModal
                    isOpen={isProfileOpen}
                    onClose={() => setIsProfileOpen(false)}
                    person={personFull}
                    connectionsContent={
                        connections ? (
                            <CandidateConnectionsTrustedCircle
                                connections={connections}
                                companiesById={companiesById}
                                seedPersonLitesById={personById}
                                recommendationsBySeedId={recByPersonId}
                            />
                        ) : undefined
                    }
                />
            )}
        </div>
    )
}
